import React, { useRef, useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Platform, ScrollView, View, useWindowDimensions, Pressable } from 'react-native';
import { Text } from '@constants/Themed';
import AnimatedLottieView from '@utils/lottieAnimation';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import onboardSchema from '@components/modals/TutorialModal/utils/onboardSchema';
import { Separator, XStack } from 'tamagui';
import Button from '@components/UI/Button/Button';
import ActionArray from '@components/UI/ContextActions.tsx/ActionArray';
import { SuggestedIntegrationsList } from './SuggestedIntegrationCard';
import { SuggestedModelsList } from './SuggestedModelCard';
import LottieView from 'lottie-react-native';
import { AntDesign } from '@expo/vector-icons';

export interface FormMessageHandle {
  submit: () => void;
  skip: () => void;
  reset: () => void;
  isFormActive: () => boolean;
}

const mapInitialValuesToSchema = (initialValues: any, schema: any) => {
  const mappedValues: { [key: string]: any } = {};
  if (Array.isArray(schema)) {
    schema.forEach(field => {
      if (initialValues.hasOwnProperty(field.title)) {
        mappedValues[field.title] = initialValues[field.title];
      }
    });
  } else {
    console.warn("⚠️ Schema is not an array in mapInitialValuesToSchema:", schema);
  }
  return mappedValues;
};

interface FormMessageProps {
  /**
   * @param currentMessage - The current message to be displayed
   * @default {}
   */
  currentMessage: any;
  /**
   * @param theme - The theme of the form
   * @default false
   */
  theme: boolean;
  /**
   * @param onboardingStage - The stage of the onboarding
   * @default null
   */
  onboardingStage: 'welcome' | 'tags' | 'business_model' | 'ai_analysis' | 'profile' | 'context' | 'models' | null;
  /**
   * @param onSubmit - The function to be called when the form is submitted
   * @param values - The values to be passed to the form
   * @param formId - The id of the form
   * @default undefined
   */
  onSubmit: (values: any, formId?: string) => void;
  /**
   * @param onSkip - The function to be called when the skip button is pressed
   * @default undefined
   */
  onSkip?: () => void;
  /**
   * @param onModelSelection - The function to be called when models are selected
   * @default undefined
   */
  onModelSelection?: (selectedModels: any[]) => void;
  /**
   * @param onModelSkip - The function to be called when model selection is skipped
   * @default undefined
   */
  onModelSkip?: () => void;
  /**
   * @param styles - The styles to be passed to the form
   */
  styles: any;
  /**
   * @param initialValues - The initial values to be passed to the form
   * @default {}
   */
  initialValues?: any;
  /**
   * @param delayFormLoad - Delay before rendering the form (in seconds)
   * @default 0
   */
  delayFormLoad?: number;
  /**
   * @param hideButtons - Hide the form action buttons (they will be rendered elsewhere)
   * @default false
   */
  hideButtons?: boolean;
}

const FormMessage = forwardRef<FormMessageHandle, FormMessageProps>(({
  currentMessage,
  theme,
  initialValues = {},
  onboardingStage,
  onSubmit,
  onSkip,
  onModelSelection,
  onModelSkip,
  styles,
  delayFormLoad = 0,
  hideButtons = false
}, ref) => {
  const { height: screenHeight } = useWindowDimensions();
  const scrollViewRef = useRef<any>(null);
  const formikRef = useRef<any>(null);
  const [selectedModelIds, setSelectedModelIds] = React.useState<string[]>([]);

  // Enhanced submit function with comprehensive logging
  const handleSubmit = useCallback(async (values: any, formId?: string) => {

    // Try to access Formik state if ref is available
    if (formikRef.current) {
      const formikState = formikRef.current;


      // Check for validation errors
      const validationErrors = formikState.errors || {};
      const errorKeys = Object.keys(validationErrors);
      if (errorKeys.length > 0) {

      } else {
        console.log("✅ No validation errors found");
      }

      // Check touched fields
      const touchedFields = formikState.touched || {};
      const touchedKeys = Object.keys(touchedFields).filter(key => touchedFields[key]);
    } else {
      console.warn("⚠️ Formik ref not available - cannot access form state");
    }

    try {
      const result = await onSubmit(values, currentMessage?._id);
    } catch (error) {

      throw error; // Re-throw to let GenerateSchemaForm handle it
    }
  }, [currentMessage, onSubmit]);

  // Reduce padding for integration-only messages (no form schema)
  const hasOnlyIntegrations = !currentMessage.formSchema && currentMessage.suggestedIntegrations && Array.isArray(currentMessage.suggestedIntegrations) && currentMessage.suggestedIntegrations.length > 0;
  const hasOnlyModels = !currentMessage.formSchema && currentMessage.suggestedModels && Array.isArray(currentMessage.suggestedModels) && currentMessage.suggestedModels.length > 0;

  const handleModelToggle = React.useCallback((modelId: string) => {
    setSelectedModelIds((prev) => {
      if (prev.includes(modelId)) {
        return prev.filter((id) => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  }, []);

  const handleModelContinue = React.useCallback(() => {
    if (currentMessage.suggestedModels && selectedModelIds.length > 0) {
      const selectedModels = currentMessage.suggestedModels.filter((model: any) => {
        const modelId = model._id || model.schemaTitle || 'unknown';
        return selectedModelIds.includes(modelId);
      });
      onModelSelection?.(selectedModels);
    }
  }, [currentMessage.suggestedModels, selectedModelIds, onModelSelection]);

  const handleModelSkip = React.useCallback(() => {
    onModelSkip?.();
  }, [onModelSkip]);

  // Expose form actions via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      formikRef.current?.submitForm();
    },
    skip: () => {
      onSkip?.();
    },
    reset: () => {
      formikRef.current?.resetForm();
    },
    isFormActive: () => {
      return !currentMessage.formSubmitted && !!currentMessage.formSchema;
    }
  }), [onSkip, currentMessage.formSubmitted, currentMessage.formSchema]);

  return (
    <View
      style={{
        padding: (hasOnlyIntegrations || hasOnlyModels) ? 10 : 10,
        paddingBottom: (hasOnlyIntegrations || hasOnlyModels) ? 5 : 10,
        marginTop: (hasOnlyIntegrations || hasOnlyModels) ? 20 : 30,
        alignSelf: 'flex-start',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        alignContent: 'stretch',
        flexDirection: 'row'
      }}
    >
      <LottieView source={require("@assets/animations/automation_robot.json")} autoPlay loop style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-start", marginTop: 10 }} />
      <View style={[styles.systemMessageBubble, {
        backgroundColor: theme ? '#ececec' : '#2a2a2a',
        width: "100%",
        minWidth: "20dvw",
        maxWidth: "93dvw",
        alignSelf: 'flex-start',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        paddingBottom: (hasOnlyIntegrations || hasOnlyModels) ? 10 : undefined,
        ...(currentMessage.formSubmitted ? { flex: 0, minHeight: 0 } : {})
      }]}>
        {currentMessage.text && (
          <View style={{ paddingHorizontal: 20, marginBottom: 10 }}>
            <Text style={{ opacity: !currentMessage.formSubmitted ? 1 : 0.4, fontWeight: '600', fontSize: 16 }}>
              {currentMessage.text}
            </Text>
            {/* Retry/Reload button for model suggestions */}
            {currentMessage.canRetry && currentMessage.retryFunction && (
              <Pressable
                onPress={() => currentMessage.retryFunction?.()}
                style={{
                  marginTop: 12,
                  paddingVertical: 8,
                  paddingHorizontal: 16,
                  backgroundColor: theme ? '#3b82f6' : '#2563eb',
                  borderRadius: 8,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 8,
                  alignSelf: 'flex-start',
                }}
              >
                <AntDesign name="reload" size={14} color="white" />
                <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                  {currentMessage.isExampleFallback ? 'Reload Models' : 'Retry'}
                </Text>
              </Pressable>
            )}
          </View>
        )}

        {/* Only show form controls if there's actually a form schema and buttons are not hidden */}
        {
          !hideButtons && !currentMessage.formSubmitted && currentMessage.formSchema && (
            <View style={{ flex: 1, width: "100%", maxHeight: 50, marginBottom: 10 }}>
              <XStack transform={[{ scale: 0.8 }]} left={Platform.select({ web: -10, native: -20 })} minWidth="100%" height="100%" alignSelf="stretch" gap={10}>
                {/* Skip Button */}
                <ActionArray
                  conjoined={true}
                  wrap
                  // forceRow={true}
                  activeComponent={currentMessage.formSubmitted ? "submit" : "skip"}
                  actionArray={[{
                    functionName: "skip",
                    function: () => {
                      onSkip?.();
                    },
                    text: "Skip",
                    icon: "step-forward",
                  },
                    {
                      functionName: "reset",
                      function: () => {
                        formikRef.current?.resetForm();
                      },
                      text: "Reset",
                      icon: "redo",
                    },
                    {
                      functionName: "submit",
                      function: () => {
                        formikRef.current?.submitForm();
                      },
                      text: "Submit",
                      icon: "play-circle",
                    }
                  ]} />

              </XStack>
            </View>
          )
        }
        {!currentMessage.formSubmitted && currentMessage.formSchema && (
          <View style={{ alignSelf: 'flex-start', justifyContent: 'flex-start', alignItems: 'flex-start', alignContent: 'flex-start', minWidth: '100%' }}>
            {
              Platform.OS === 'web' ? (
                <WebScrollableContainer minHeight={350}>
                  <GenerateSchemaForm
                    ref={formikRef}
                    hideButton={true}
                    schema={currentMessage.formSchema || onboardSchema}
                    noBackground
                    initialData={mapInitialValuesToSchema(initialValues, currentMessage.formSchema || onboardSchema) || currentMessage.formValues || {}}
                    submitFunction={handleSubmit}
                    nullify={['_id', 'companyId', 'createdAt', 'updatedAt', '__v', 'createdBy']}
                    customStyling={{ minHeight: 350, paddingBottom: 0 }}
                    inlineFields={true} />
                </WebScrollableContainer>
              ) : (
                  <ScrollView
                    ref={scrollViewRef}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{ paddingRight: 20, flexGrow: 1, minHeight: 350 }}
                    onStartShouldSetResponderCapture={() => true}
                    bounces={true}
                    alwaysBounceVertical={false}
                    // Ensure this ScrollView can capture gestures even when nested in FlatList
                  >
                    <GenerateSchemaForm
                      ref={formikRef}
                      // hideButton={true}
                      schema={currentMessage.formSchema || onboardSchema}
                      noBackground
                      initialData={mapInitialValuesToSchema(initialValues, currentMessage.formSchema || onboardSchema) || currentMessage.formValues || {}}
                      retainInitialValues={true}
                      submitFunction={handleSubmit}
                      nullify={['_id', 'companyId', 'createdAt', 'updatedAt', '__v', 'createdBy']}
                      buttonLabel={currentMessage.showProfileForm ? "Continue to Next Step" : "Submit Details"}
                      scrollEnabled={false}
                      avoidKeyboardDisabled={true}
                      customStyling={{ minHeight: 350, paddingBottom: 0 }}
                      showSkipButton={onboardingStage === 'tags'}
                      skipButtonLabel="Skip"
                      onSkip={onSkip}
                      inlineFields={true}
                    />
                  </ScrollView>
              )
            }
          </View>
        )
        }
        {
          currentMessage.formSubmitted && (
            <Text style={{ minHeight: 70, minWidth: "20dvw", textAlign: 'center', fontSize: 16, fontWeight: '600', marginBottom: 10 }}>
              Form submitted successfully
            </Text>
          )
        }

        {/* Render suggested models if present - show them prominently */}
        {currentMessage.suggestedModels &&
          Array.isArray(currentMessage.suggestedModels) &&
          currentMessage.suggestedModels.length > 0 && (
            <View style={{ marginTop: currentMessage.formSchema ? 20 : 8, width: '100%', marginBottom: 0 }}>
              <SuggestedModelsList
                models={currentMessage.suggestedModels}
                theme={theme}
                selectedModelIds={selectedModelIds}
                onModelToggle={handleModelToggle}
                onContinue={handleModelContinue}
                onSkip={handleModelSkip}
              />
            </View>
          )}

        {/* Render suggested integrations if present - show them prominently */}
        {currentMessage.suggestedIntegrations &&
          Array.isArray(currentMessage.suggestedIntegrations) &&
          currentMessage.suggestedIntegrations.length > 0 && (
            <View style={{ marginTop: currentMessage.formSchema ? 20 : 8, width: '100%', marginBottom: 0 }}>
              <SuggestedIntegrationsList
                integrations={currentMessage.suggestedIntegrations}
                theme={theme}
              />
            </View>
          )}
      </View>
    </View>
  );
});

FormMessage.displayName = 'FormMessage';

const WebScrollableContainer: React.FC<{
  minHeight: number
  children: React.ReactNode
}> = ({ minHeight, children }) => {
  const ref = React.useRef<HTMLDivElement>(null)

  const onWheelCapture = (e: React.WheelEvent) => {
    const el = ref.current
    if (!el) return

    const { scrollTop, scrollHeight, clientHeight } = el
    const delta = e.deltaY

    const canScrollUp = scrollTop > 0
    const canScrollDown = scrollTop + clientHeight < scrollHeight

    const shouldConsume =
      (delta < 0 && canScrollUp) ||
      (delta > 0 && canScrollDown)

    if (shouldConsume) {
      e.stopPropagation(); // ← ONLY here
    }
    // else: let GiftedChat handle it
  }

  return (
    <div
      ref={ref}
      onWheelCapture={onWheelCapture}
      style={{
        ...(minHeight > 0 ? { minHeight } : {}),
        paddingRight: 20,
        overflowY: 'auto',
        overflowX: 'hidden',
        alignSelf: 'stretch',
        overscrollBehavior: 'contain',
      }}
    >
      {children}
    </div>
  )
}


export default FormMessage;

